import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "@/app/routes/paths";
import type { Trip } from "@/entities/trip";
import { offerRefusalCopy } from "@/features/trips";
import { subscribeOfferSuperseded } from "@/shared/realtime";
import { useDialog } from "@/shared/ui";
import { useAgentTripsFeed } from "../hooks/useAgentOffers";
import { useOfferResponse } from "../hooks/useOfferResponse";
import { OfferSheet } from "./OfferSheet";

/**
 * Point de montage de l'offre en cours, pour TOUT l'espace agent.
 *
 * Une offre doit atteindre la Main où qu'elle soit : elle a cinq minutes pour
 * répondre, et ignorer l'offre l'immobilise jusqu'à expiration (le serveur la
 * passe `BUSY` dès l'ÉMISSION, pas à l'acceptation). Le placer dans le shell
 * plutôt que sur l'accueil évite qu'une offre passe inaperçue parce que la Main
 * consultait son historique.
 *
 * Une seule offre à la fois : c'est ce que la rotation produit, et empiler deux
 * feuilles modales sur un décompte de quatre-vingt-dix secondes serait
 * ingérable au guidon.
 */
export function OfferHost() {
  const { offers } = useAgentTripsFeed();
  const [dismissed, setDismissed] = useState<string | null>(null);

  // ── L'offre est partie à quelqu'un d'autre ────────────────────────────────
  //
  // §1 du contrat du 2026-09-02 : la course est proposée à TROIS Mains à la
  // fois, et la première qui accepte l'emporte. Sans cette écoute, la feuille
  // reste ouverte sur une course déjà partie — la Main l'accepte, reçoit un
  // `409`, et découvre l'échec au moment où elle croyait avoir gagné. Pire si
  // elle a commencé à rouler.
  //
  // On ferme SANS rien annoncer : la Main n'a rien fait, rien perdu, et une
  // alerte pour une course qu'elle n'avait pas encore acceptée serait du bruit.
  useEffect(
    () =>
      subscribeOfferSuperseded((p) => {
        if (p.trip_id) setDismissed(p.trip_id);
      }),
    [],
  );

  const offer = offers.find((o) => o.id && o.id !== dismissed);
  if (!offer?.id) return null;

  return (
    <OfferResponder
      // `key` : deux offres successives repartent d'un état neuf — motif de
      // refus vidé, décompte réamorcé.
      key={offer.id}
      offer={offer}
      onDismiss={() => setDismissed(offer.id ?? null)}
    />
  );
}

function OfferResponder({ offer, onDismiss }: { offer: Trip; onDismiss: () => void }) {
  const tripId = offer.id ?? "";
  const navigate = useNavigate();
  const { alert } = useDialog();
  const { accept, decline, isPending } = useOfferResponse(tripId);

  const handleOutcome = async (
    result: Awaited<ReturnType<typeof accept>>,
    contexte: "accept" | "decline",
  ) => {
    switch (result.outcome) {
      case "ouvrir":
        onDismiss();
        navigate(paths.agentTrip(tripId));
        return;

      case "fermer": {
        onDismiss();
        // ⚠️ Ni panne, ni erreur d'authentification — y compris sur un `403`,
        // qui signifie seulement que l'offre est passée à une autre Main. La
        // présenter comme un incident inquiéterait pour rien.
        if (contexte !== "accept") return;

        // §2 : le même `409` recouvre deux situations OPPOSÉES. « Une autre a
        // été plus rapide » ne demande rien ; « vous n'êtes plus disponible »
        // demande de terminer la course en cours. Le même texte pour les deux
        // enverrait la Main attendre une offre qui ne viendra pas, puisque
        // c'est elle-même qui se bloque.
        const kind = result.refusal ?? "autre";
        const copy = offerRefusalCopy(kind);
        void alert({
          // Une course déjà prise est l'issue normale d'une offre disputée à
          // trois ; être soi-même bloqué appelle une action.
          tone: kind === "deja_occupee" ? "warning" : "info",
          title: copy.title,
          // Le serveur rédige pour l'utilisateur : son message passe devant.
          message: result.message ?? copy.message,
        });
        return;
      }

      case "corriger":
        void alert({
          tone: "danger",
          title: "Motif requis",
          message: result.message ?? "Indique un motif d'au moins 3 caractères.",
        });
        return;

      default:
        void alert({
          tone: "danger",
          title: "Envoi impossible",
          message: result.message ?? "Vérifie ta connexion et réessaie.",
        });
    }
  };

  return (
    <OfferSheet
      trip={offer}
      isResponding={isPending}
      onClose={onDismiss}
      onAccept={() => void accept().then((r) => handleOutcome(r, "accept"))}
      onDecline={(reason) => void decline(reason).then((r) => handleOutcome(r, "decline"))}
    />
  );
}
